import React, { useEffect, useState, useRef } from 'react'

import { useNavigate, useParams, useLocation, Location, Link } from 'react-router-dom'
import { PDFDownloadLink } from '@react-pdf/renderer'
import moment from 'moment'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faAngleDown, faCircleUser, faCodeBranch, faDownload, faEllipsisVertical, faExclamationTriangle, faHand, faHistory, faInfoCircle, faSpinner, faTrash, faWarning } from '@fortawesome/free-solid-svg-icons'
import { toastError, toastSuccess } from './services/Toast'
import isEqual from 'lodash.isequal'

import { User } from 'hooks/useUsers'
import { ProductSearchResult, PipeOpening, FlowControlDevice, Metadata, MetadataStatusType, MetadataInfo, EDITS_REQUESTED, PENDING_REVIEW, APPROVED } from 'models/SearchResult'
import { DATE_TIME_FORMAT, PAID_ADD_ONS, Weight } from 'constants/Constants'
import config from 'config'
import { PageLoadSpinner } from 'components/spinners/PageLoadSpinner'
import { initialPipeOpening, ProductFormState, useProductForm } from 'hooks/useProductForm'
import { ProductForm } from 'components/forms/ProductForm'
import { DesignDocument, DesignDocumentFormState, useDesignDocumentsForm } from 'hooks/useDesignDocumentsForm'
import { usePipeOpenings } from 'hooks/usePipeOpenings'
import { usePipeMaterials } from 'hooks/usePipeMaterials'
import { useProductTypes } from 'hooks/useProductTypes'
import { Permissions } from 'AppController'
import { BaseModal } from 'components/modals/BaseModal'
import { TextAreaInputField } from 'components/inputs/TextAreaInputField'
import { ProductionAndQualityChecklist, productToChecklistData } from 'components/PDF/ProductionAndQualityChecklist'
import { isMerchant } from 'models/User'
import { useProductVersions } from 'hooks/useProductVersions'
import { IssueNumberTag, VersionHistoryModal } from 'pages/manage/products/VersionHistoryModal'
import { useFlowControlDevices } from 'hooks/useFlowControlDevices'
import { diffProductSearchResultAndProductFormState, isAllDrawingsDifferent, WhatsChanged } from 'pages/manage/products/WhatsChanged'
import { ConfirmActionModal } from 'components/modals/ConfirmActionModal'
import { prettyDateFromTimestamp } from 'pages/manage/products/Comment'
import { useAuth0Authentication } from 'hooks/useAuth0Authentication'

const getApi = (productCode: string, accessToken: string): Promise<Response> => {
    return fetch(
    `${config.apiUrl}/products/get/${productCode}`
        , {
        method: 'GET',
        redirect: 'follow',
        headers: {
            Authorization: `Bearer ${accessToken}`,
            "Content-Type": 'application/json'
        },
        }); 
  }

const IssuesDropwdown = ({ metadata, productCode}: { metadata?: Metadata, productCode: string }) => {

    const lastUpissued: MetadataInfo | undefined= metadata?.lastUpissued
    const upissuesLength: number = metadata?.upissues.length
    const issueNumber: number = metadata.issueNumber

    return(
        <div className="dropdown is-hoverable is-pulled-right">
            <div className="dropdown-trigger">
                <button 
                    className="has-tooltip-arrow has-tooltip-multiline button is-rounded is-light has-text-weight-bold" 
                    aria-haspopup="true" 
                    aria-controls="dropdown-menu" 
                    data-tooltip={lastUpissued && `upissued by ${lastUpissued?.byFullName} on ${prettyDateFromTimestamp(lastUpissued?.timestamp)}`}>
                        
                    <span>
                        <FontAwesomeIcon className="pr-1" size="1x" icon={faCodeBranch} /> 
                        {`v${issueNumber || 1}`} 
                        {upissuesLength > 1 &&<FontAwesomeIcon className="pl-3" size="1x" icon={faAngleDown} /> }
                    </span>

                 </button>
            </div>
            {upissuesLength > 1 &&
                <div className="dropdown-menu" id="dropdown-menu" role="menu">
                    <div className="dropdown-content">
                        {metadata?.upissues
                            ?.sort((a, b) => b - a)
                            .slice(1, upissuesLength)
                            .map((v, idx) => (
                                <Link key={`issueNumber-${idx}`} className="dropdown-item" target='_blank' to={`/view/product/${productCode}/versions/${v}`}>
                                    v{issueNumber - idx - 1}
                                </Link>
                            ))
                        }
                    </div>
                </div>
            }
        </div>
    )
}

export interface IndexProductDocument {
    objectID: string,
    productCode: string,
    description: string,
    productType: string,
    flowControlDevices: FlowControlDevice[],
    secondaryCasting: string[],
    toe: string,
    steelworkCode: string[],
    reinforcing: string[],
    handrail: string,
    pipeOpenings: PipeOpening[],
    weight?: number,
    legacyProductCode: string,
    designDocuments: DesignDocument[],
    tags: string[],
    metadata: Metadata
}

const createIndexProductDocument = (productFormState: ProductFormState, designDocumentFormState: DesignDocumentFormState[], metadata: Metadata): IndexProductDocument => {
    return {
        objectID: productFormState.productCode,
        productCode: productFormState.productCode,
        description: productFormState.description,
        productType: productFormState.productType,
        flowControlDevices: productFormState.flowControlDevices,
        secondaryCasting: productFormState.secondaryCasting,
        toe: productFormState.toe,
        steelworkCode: productFormState.steelworkCode,
        reinforcing: productFormState.reinforcing,
        handrail: productFormState.handrail === `KQ` || productFormState.handrail === `PQ` 
            ? `${productFormState.handrail}-${productFormState.legacyProductCode}` 
            : productFormState.handrail,
        pipeOpenings: productFormState.pipeOpenings.filter(_ => !isEqual(_, initialPipeOpening)),
        ...(productFormState.weight && { weight: Number(productFormState.weight) }),
        legacyProductCode: productFormState.legacyProductCode,
        tags: productFormState.tags,
        designDocuments: designDocumentFormState.map((doc) => ({
            ...doc,
            category: doc.category.category,
            asset: {
                assetPath: doc.asset.filePath,
                assetFileName: doc.asset.fileName,
                assetFileExtension: doc.asset.fileExtension
            } 
        })),
        ...(metadata && { metadata: metadata })
    }
}

export interface FormInputError {
    isError: boolean,
    errorMessage: string
}

type ReactRouterLocationState = {
    duplicateState: ProductSearchResult,
    from?: 'EDITS_REQUESTED' | 'UPISSUE_REQUESTED' | 'PENDING_REVIEW_EDIT',
}

const initialRequestEditsForm = { comment: '' }

interface RequestEditsForm {
  comment?: string
}

const initialChangeRequestForm = { comment: '', isUpissue: false }

interface ChangeRequestForm {
    isUpissue:boolean,
  comment: string
}

interface ChangeRequestFormErrorState {
    isUpissue: FormInputError,
    comment: FormInputError,
}

const initialFormError: FormInputError = {
    isError: false,
    errorMessage: null
}

const initialChangeRequestFormErrorState: ChangeRequestFormErrorState = {
    isUpissue: initialFormError, 
    comment: initialFormError,
}

const initialUpissueForm = { comment: '' }

interface UpissueForm {
    comment?: string
}

const IndexProductScreen = ({ loggedInUser, pageState = 'add'}:{ loggedInUser: User, pageState?: 'add' | 'edit' | 'view'}) => {
        
    const { productCode } = useParams()
    const { getAuth0AccessToken } = useAuth0Authentication()
    const navigate = useNavigate()

    const [isConfirmDeleteModalActive, setIsConfirmDeleteModalActive] = useState<boolean>(false)
    const [isRequestedEditsModalActive, setIsRequestedEditsModalActive] = useState<boolean>(false)
    const [isChangeRequestModalActive, setIsChangeRequestModalActive] = useState<boolean>(false)
    
    const [isUpissueModalActive, setIsUpissueModalActive] = useState<boolean>(false)
    const [isNewProductLineModalActive, setIsNewProductLineModalActive] = useState<boolean>(false)
    const [isRequestLoading, setIsRequestLoading] = useState<boolean>(false)
    const [isChangeRequestLoading, setIsChangeRequestLoading] = useState<boolean>(false)
    const [isVersionHistoryModalActive, setIsVersionHistoryModalActive] =useState<boolean>(false)

    const hasLoadedDuplicate = useRef(false)
    const [editObject, setEditObject] = useState<ProductSearchResult>(null)
    const [requestedEditsForm, setRequestedEditsForm] = useState<RequestEditsForm>(initialRequestEditsForm)
    const [upIssueForm, setUpissueForm] = useState<UpissueForm>(initialUpissueForm)

    const [changeRequestForm, setChangeRequestForm] = useState<ChangeRequestForm>(initialChangeRequestForm)
    const [changeRequestFormErrorState, setChangeRequestFormErrorState] = useState<ChangeRequestFormErrorState>(initialChangeRequestFormErrorState)

    const {productVersions, isLoading: isProductVersionsLoading, getProductVersions } = useProductVersions()
    const [isUpissue, setIsUpissue] = useState<boolean>(false)
    const [isNewProductLine, setIsNewProductLine] = useState<boolean>(false)

    const productForm = useProductForm() 
    
    const { 
        validateForm: validateProductForm, 
        formState: productFormState, 
        loadFormState: loadProductFormState,
        resetFormState: resetProductFormState,
        buildProductCode,
        buildLegacyProductCode,
        initialPipeOpening
    } = productForm

    const designDocumentsForm = useDesignDocumentsForm() 
    const { 
        validateForm: validateDesignDocumentsForm, 
        formState: designDocumentsState, 
        loadFormState: loadDesignDocumentsFormState,
        resetFormState: resetDesignDocumentsState
     } = designDocumentsForm

    const { pipeOpenings } = usePipeOpenings()
    const { pipeMaterials } = usePipeMaterials()
    const { productTypes, getProductType } = useProductTypes()
    const { flowControlDevices } = useFlowControlDevices()

    // We use location to pass a duplicate state to this component for the user action of duplicating a product.
    // We then load this into the form state, but with a new product code (by using a new generated id rather that one
    // from previous product as we want it to be unique.)
    const location: Location = useLocation()
    const locationState: ReactRouterLocationState | null = location.state as ReactRouterLocationState

    const resetAllState = () => {
        resetProductFormState()
        setEditObject(null)
        resetDesignDocumentsState()
    }

    const initialiseEditObject = (editObject: ProductSearchResult, isDuplicate: boolean = false) => {
        
        setEditObject(editObject)
        loadProductFormState({
            ...editObject,
            ...(isDuplicate && { 
                productCode: buildProductCode(editObject.productType),
                legacyProductCode: buildLegacyProductCode(getProductType(editObject.productType).legacyCode),
                handrail: /^(KQ|PQ)-/.test(editObject.handrail) ? editObject.handrail.slice(0, 2) : editObject.handrail,
            })
        })
        loadDesignDocumentsFormState(editObject.designDocuments)
    }

    useEffect(() => {

        if (pageState !== 'add') {
            const get = async (id: string) => {

                const accessToken: string = await getAuth0AccessToken()
                const response = await getApi(id, accessToken)
                const getResponse: ProductSearchResult = await response.json()

                if (!response.ok) {
                    navigate(`/`)
                }

                initialiseEditObject(getResponse)
            }

            get(productCode)
        } else {
            resetAllState()
        }
        
    }, [pageState !== 'add'])

    // Load the duplicated product into the form once product types are available
    // (we need legacyCode to build the duplicate's codes), generating a new id so it's unique.
    useEffect(() => {
        if (!locationState?.duplicateState) return
        if (!productTypes || hasLoadedDuplicate.current) return

        hasLoadedDuplicate.current = true
        initialiseEditObject(locationState.duplicateState, true)

    }, [locationState?.duplicateState, productTypes])

    // Clear the form when we leave duplicate mode.
    // Deliberately NOT keyed on productTypes: this calls resetAllState(), which nulls
    // editObject. If product types loading re-triggered it, it would wipe the product
    // fetched by the effect above and strand /edit on a permanent spinner.
    useEffect(() => {
        if (locationState?.duplicateState) return

        hasLoadedDuplicate.current = false
        resetAllState()

    }, [locationState?.duplicateState])


    // TODO - we should try move most of this off to the API if we can
    const buildMetadata = (pageState: 'add' | 'edit' | 'view', isUpissue: boolean): Metadata => {

        const metadata: MetadataInfo = {
            byUserId: loggedInUser.user_id,
            byFullName: loggedInUser.name,
            timestamp: moment.utc().format(DATE_TIME_FORMAT)
        }
 
        if (pageState === 'edit'  && isUpissue) {

            const newStatus: MetadataStatusType = PAID_ADD_ONS.quarantineZone.isEnabled ? PENDING_REVIEW : APPROVED 

            return {
                ...editObject?.metadata,
                status: newStatus,
                activeAction: { 
                    type: 'UPISSUE_PRODUCT',
                    initiated: metadata,
                    initiatedOnVersion: editObject?.metadata.version,
                },
                lastEdited: {
                    ...metadata, 
                    comment: requestedEditsForm.comment
                }
            }
        
        // if the edit is from an upissue product, we shouldn't override activeAction.
        } else if (pageState === 'edit'  && editObject?.metadata?.activeAction?.type === 'UPISSUE_PRODUCT') {

            const newStatus: MetadataStatusType = PAID_ADD_ONS.quarantineZone.isEnabled ? PENDING_REVIEW : APPROVED 

            return {
                ...editObject?.metadata,
                status: newStatus,
                lastEdited: metadata,
            }

        } else if (pageState === 'edit' && editObject?.metadata?.activeAction?.type.startsWith('CHANGE_REQUEST')) {

            const newStatus: MetadataStatusType = PAID_ADD_ONS.quarantineZone.isEnabled ? PENDING_REVIEW : APPROVED 

            return {
                ...editObject?.metadata,
                status: newStatus,
                lastEdited: {
                    ...metadata, 
                    comment: requestedEditsForm.comment
                }
            }

        // Note: Normal edit 
        } else {

            const newStatus: MetadataStatusType = PAID_ADD_ONS.quarantineZone.isEnabled ? PENDING_REVIEW : APPROVED 

            // initial edit
            if (pageState === 'edit' && editObject && !editObject?.metadata?.activeAction && editObject?.metadata?.status === APPROVED) {

                return {
                    ...editObject?.metadata,
                    status: newStatus,
                    activeAction: { 
                        type: 'EDIT_PRODUCT_LINE',
                        initiated: metadata,
                        initiatedOnVersion: editObject?.metadata.version,
                    },
                    lastEdited: metadata,
                }
            // there's an active action ongoing or it's a legacy product already in manage products so don't overwrite activeAction.
            } else if (pageState === 'edit' && editObject) {

                return {
                    ...editObject?.metadata,
                    status: newStatus,
                    lastEdited: {
                        ...metadata, 
                        ...(locationState && locationState.from === EDITS_REQUESTED && {comment: requestedEditsForm.comment})
                    }
                }
            } else if (pageState === 'add') {
    
                return {
                    status: newStatus,
                    activeAction: { 
                        type: isNewProductLine ? 'ADD_NEW_PRODUCT_LINE' : 'ADD_EXISTING_PRODUCT_LINE',
                        initiated: metadata,
                        initiatedOnVersion: 1,
                    },
                    lastEdited: metadata
                }
            } else null

        }
        
    }

    const validateForm = (): boolean => {

        const selectedProductType = getProductType(productFormState.productType)
        const isValidProductForm: boolean = validateProductForm(selectedProductType?.weightMode ?? 'CONSTANT')
        const isValidDesignDocForm: boolean = validateDesignDocumentsForm()

        return isValidProductForm && isValidDesignDocForm
    }

    const onSubmitForm = async (e: React.MouseEvent) => {
        e.preventDefault()

        const isFormValid: boolean = validateForm()

        if (isFormValid) {

            setIsRequestLoading(true)

            const indexMetadata: Metadata = buildMetadata(pageState, isUpissue)
            const indexDoc: IndexProductDocument = createIndexProductDocument(productFormState, designDocumentsState, indexMetadata)

            const accessToken: string = await getAuth0AccessToken()

            // ADDING PRODUCT
            if (pageState === 'add') {

                const response = await fetch(
                    `${config.apiUrl}/products/add`
                        , {
                        method: 'POST',
                        redirect: 'follow',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            "Content-Type": 'application/json'
                        },
                        body: JSON.stringify(indexDoc)
                    }); 
    
                if (response.status === 201) {
                    toastSuccess(`Success, product added.`)
                    setIsNewProductLineModalActive(false)
                    setTimeout(() => {
                        setIsRequestLoading(false)
                        navigate(`/manage/products/?q=${indexDoc.productCode}`)
                    }, 2000);
                      
                } else {
                    toastError()
                    setIsRequestLoading(false)
                }

            // UPISSUING PRODUCT
            } else if (pageState === 'edit' && isUpissue) {

                const response = await fetch(
                    `${config.apiUrl}/products/edit`
                        , {
                        method: 'POST',
                        redirect: 'follow',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            "Content-Type": 'application/json'
                        },
                        body: JSON.stringify(indexDoc)
                    }); 
    
                if (response.status === 201) { // TODO: should be 200
                    toastSuccess(`Success, product upissued.`)
                    setIsUpissueModalActive(false)
                    setUpissueForm(initialUpissueForm)
                    if (isUpissue) {
                        setTimeout(() => {
                            setIsRequestLoading(false)
                            navigate(`/manage/products/?q=${productCode}`)
                        }, 2000);
                    } else {
                        setIsRequestLoading(false)
                    }
                      
                } else {
                    toastError()
                    setIsRequestLoading(false)
                }

            // EDITING PRODUCT
            } else if (pageState === 'edit') {

                const response = await fetch(
                    `${config.apiUrl}/products/edit`
                        , {
                        method: 'POST',
                        redirect: 'follow',
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            "Content-Type": 'application/json'
                        },
                        body: JSON.stringify(indexDoc)
                    }); 
    
                if (response.status === 201) {
                    toastSuccess(`Success, product edited.`)    
                    setIsRequestedEditsModalActive(false)
                    setRequestedEditsForm(initialRequestEditsForm)
                    if(locationState && locationState?.from === `UPISSUE_REQUESTED`) {
                        setTimeout(() => {
                            setIsRequestLoading(false)
                            navigate(`/manage/products?q=${productCode}`)
                          }, 2000);
                    } else if (locationState && locationState.from === `PENDING_REVIEW_EDIT`) {
                        setTimeout(() => {
                            setIsRequestLoading(false)
                            navigate(`/manage/products?q=${productCode}`)
                          }, 2000);
                    } else if (locationState && (locationState?.from === EDITS_REQUESTED)) {
                        setTimeout(() => {
                            setIsRequestLoading(false)
                            navigate(`/manage/products?q=${productCode}`)
                          }, 2000);
                    } else {
                        setTimeout(() => {
                            setIsRequestLoading(false)
                            navigate(`/manage/products?q=${productCode}`)
                          }, 2000);
                    }
                      
                } else {
                    toastError()
                    setIsRequestLoading(false)
                }

            } else {
                console.error("We should never get here")
            }
        }
    }

    const validateChangeRequestForm = (): boolean => {

        const mustNotBeEmptyError: string = `Must not be empty`

        const formErrors: ChangeRequestFormErrorState =  Object.entries(changeRequestForm).reduce((acc, [key, value]) => {

            if (key === 'isUpissue') return {
                ...acc,
                [key]: {
                    isError: false,
                    errorMessage: null
                }
            }

            return {
                ...acc,
                [key]: {
                    isError: changeRequestForm.comment === null || changeRequestForm.comment === '',
                    errorMessage: changeRequestForm.comment === null || changeRequestForm.comment === '' ? mustNotBeEmptyError : null,
                }
            }
        }, initialChangeRequestFormErrorState)

        setChangeRequestFormErrorState(formErrors)

        return Object.entries(formErrors).every(([key, value])=> {
            const formError: FormInputError = value
            return formError.isError === false
        })
    }

    const onSubmitChangeRequest = async (e: React.MouseEvent) => {

        e.preventDefault()

        const isFormValid: boolean = validateChangeRequestForm()

        if (isFormValid) {

            setIsChangeRequestLoading(true)

            const accessToken: string = await getAuth0AccessToken()

            const indexDoc: IndexProductDocument = {
                ...editObject,
                metadata: {
                    ...editObject?.metadata,
                    activeAction: {
                        type: changeRequestForm.isUpissue ? 'CHANGE_REQUEST_FOR_PRODUCT_REQUIRING_UPISSUE' : 'CHANGE_REQUEST_FOR_PRODUCT',
                        initiated: {
                            byUserId: loggedInUser.user_id,
                            byFullName: loggedInUser.name,
                            timestamp: moment.utc().format(DATE_TIME_FORMAT),
                            comment: changeRequestForm.comment,
                        },
                        initiatedOnVersion: editObject?.metadata.version
                    },
                    status: EDITS_REQUESTED,
                    lastModerated: {
                        byUserId: loggedInUser.user_id,
                        byFullName: loggedInUser.name,
                        timestamp: moment.utc().format(DATE_TIME_FORMAT),
                        comment: changeRequestForm.comment
                    }
                }
            }

            const response = await fetch(
                `${config.apiUrl}/products/edit`
                    , {
                    method: 'POST',
                    redirect: 'follow',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": 'application/json'
                    },
                    body: JSON.stringify(indexDoc)
                }); 

            if (response.status === 201) {
                toastSuccess(`Success, product edited.`)    
                setIsChangeRequestModalActive(false)
                setChangeRequestForm(initialChangeRequestForm)

                setTimeout(() => {
                    setIsChangeRequestLoading(false)
                    navigate(`/manage/products/edits?q=${productCode}`)
                    }, 2000);

                } else {
                    toastError()
                    setIsChangeRequestLoading(false)
                }
                  
            } 
    }
    

    const clearState = () => {
        resetProductFormState()
        resetDesignDocumentsState()
    }

    const onDeleteProduct = async () => {

        const accessToken: string = await getAuth0AccessToken()

        setIsRequestLoading(true)

        try {
            const response = await fetch(
                `${config.apiUrl}/products/get/${productCode}`
                    , {
                    method: 'DELETE',
                    redirect: 'follow',
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        "Content-Type": 'application/json'
                    },
                    body: ''
                }); 
    
            if (response.status === 200) {
                toastSuccess(`Success, product archived.`,)
                setIsRequestLoading(false)
                setIsConfirmDeleteModalActive(false)
                navigate({
                pathname: '/',
                search: '?isRefresh=true',
                }, { replace: true })
                  
            } else {
                toastError()
            }
        } catch (error) {
            toastError()
        }

        setIsRequestLoading(false)
    }

    const onClear = (e: React.MouseEvent) => {
        e.preventDefault()
        clearState()
    }

    const onCancelEdit = (e: React.MouseEvent) => {
        e.preventDefault()
        initialiseEditObject(editObject)
    }

    const onCancelRequestedEditsModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setRequestedEditsForm(initialRequestEditsForm)
        setIsRequestedEditsModalActive(false)
    }

    const onCancelUpissueModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setUpissueForm(initialUpissueForm)
        setIsUpissueModalActive(false)
    }

    const onCancelChangeRequestModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setChangeRequestForm(initialChangeRequestForm)
        setIsChangeRequestModalActive(false)
    }

    const onCancelIsNewProductLineModal = (e: React.MouseEvent) => {
        e.preventDefault()
        setIsNewProductLineModalActive(false)
    }

    const onClickShowRequestedEditsModal = (e: React.MouseEvent): void => {
        e.preventDefault();
        validateForm() && setIsRequestedEditsModalActive(true)    
    }

    const onClickIsNewProductLineModalActive = (e: React.MouseEvent): void => {
        e.preventDefault();
        validateForm() && setIsNewProductLineModalActive(true)    
    }

    const onClickShowVersionHistoryModal = () => {
        getProductVersions(productCode)
        setIsVersionHistoryModalActive(true)
    }

    const onClickShowChangeRequestModal = (e: React.MouseEvent): void => {
        e.preventDefault();
        setIsChangeRequestModalActive(true)    
    }
    
    const onChangeIsUpissue = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isUpissue: boolean = e.target.value === 'true'
        setIsUpissue(isUpissue)
    }

    const onChangeIsNewProductLine = (e: React.ChangeEvent<HTMLInputElement>) => {
        const isNewProductLine: boolean = e.target.value === 'true'
        setIsNewProductLine(isNewProductLine)
    }

    const SubmitButton = ({ isRequestLoading, isDisabled, onClick, buttonText }: { isRequestLoading: boolean, isDisabled: boolean, onClick: (e: React.MouseEvent) => void, buttonText: string }) => (
        <button className={`button is-link ${isRequestLoading ? `is-loading` : ``}`} disabled={isDisabled} onClick={onClick}>{buttonText}</button>
    )

    const renderSubmitButton = () => {

        const isDisabled = designDocumentsState && designDocumentsState.every(_ => _.asset.isLoading)
        
        if (locationState && (locationState.from === EDITS_REQUESTED) && pageState === 'edit') {
            return <SubmitButton isRequestLoading={isRequestLoading} isDisabled={isDisabled} onClick={onClickShowRequestedEditsModal} buttonText={`Edit`} />
        } else if (locationState && (locationState.from === `UPISSUE_REQUESTED` || locationState.from === `PENDING_REVIEW_EDIT`) && pageState === 'edit') {
            return <SubmitButton isRequestLoading={isRequestLoading} isDisabled={isDisabled} onClick={onSubmitForm} buttonText={`Edit`} /> 
        } else if (pageState === 'edit') {
            return <SubmitButton isRequestLoading={isRequestLoading} isDisabled={isDisabled} onClick={() => validateForm() && setIsUpissueModalActive(true)} buttonText={`Edit`} />
        } else if (pageState === 'add') {
            return <SubmitButton isRequestLoading={isRequestLoading} isDisabled={isDisabled} onClick={onClickIsNewProductLineModalActive} buttonText={`Add`} />
        } else null
    }

    if ((pageState === 'edit'  && !editObject) || !productTypes || !pipeOpenings) return(
        <div className="container is-fullheight">
            <div className="columns mt-6 pt-6">
                <div className="column is-offset-one-quarter is-half" style={{padding: '5rem', borderRadius: 6}}>
                    <PageLoadSpinner />
                </div>
            </div>
        </div>
    )

    return (
        <div className="container is-fullheight">

            {pageState !== 'add' && Permissions.canViewProductHistory &&
            <VersionHistoryModal 
                productVersions={productVersions}
                isModalActive={isVersionHistoryModalActive}
                isLoading={isProductVersionsLoading}
                loggedInUser={loggedInUser}
                setIsVersionHistoryModalActive={setIsVersionHistoryModalActive} />
            }

            {pageState === 'edit' && Permissions.canDelete(loggedInUser) &&
                <ConfirmActionModal 
                    title='Are you sure you want to archive?'
                    subtitle={`Once you archive this item, you'll still be able to access from 'Manage Products'.`}
                    Icon={<FontAwesomeIcon icon={faExclamationTriangle} size={`3x`} />}
                    confirmBtnText='Archive'
                    isModalActive={isConfirmDeleteModalActive}
                    isLoading={isRequestLoading}
                    onCancel={() => setIsConfirmDeleteModalActive(false)}
                    onConfirmAction={onDeleteProduct} />
            }

            {pageState === 'add' &&
              <BaseModal
                isModalActive={isNewProductLineModalActive}
                modalTitle={`Add Product`}
                onCancelModal={onCancelIsNewProductLineModal}>
                    <form> 
                        <div className="message">
                            <div className="message-body p-5">
                                <FontAwesomeIcon className="pr-2" icon={faInfoCircle} size={'lg'} />
                                <span>{`New product lines require a two-stage approval process. Please confirm before proceeding.`}</span> 
                            </div>                                
                        </div>
                        <div className="columns is-vcentered">
                            <div className="column">
                                <div className="field">
                                    <label className="label">Is this a new product line?</label> 
                                    <input onChange={onChangeIsNewProductLine} className="is-checkradio" id="isNewProductLineYesCheckRadio" type="radio" value="true" name="isNewProductLine" checked={isNewProductLine} />
                                    <label htmlFor="isNewProductLineYesCheckRadio">Yes</label>
                                    <input onChange={onChangeIsNewProductLine} className="is-checkradio" id="isNewProductLineNoCheckRadio" type="radio" value="false" name="isNewProductLine" checked={!isNewProductLine} />
                                    <label htmlFor="isNewProductLineNoCheckRadio">No</label>
                                </div>
                            </div>
                        </div>

                        <div className="field is-grouped is-pulled-right">
                            <div className="control">
                                <button className={`button is-link ${isRequestLoading ? `is-loading` : ``}`} disabled={designDocumentsState && designDocumentsState.every(_ => _.asset.isLoading) } onClick={onSubmitForm}>{`Submit`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={(e) => onCancelIsNewProductLineModal(e)}>Cancel</button>
                            </div>
                        </div>

                    </form>
                </BaseModal>                
            }

            {pageState === 'edit' &&
              <BaseModal
                isModalActive={isUpissueModalActive}
                modalTitle={`Edit Product`}
                onCancelModal={onCancelUpissueModal}>
                    <form> 
                        <div className="message">
                            <div className="message-body p-5">
                                <FontAwesomeIcon className="pr-2" icon={faInfoCircle} size={'lg'} />
                                <span>{`Editing a live product that has been previously manufactured will require you to upissue the product (if the headwall has been manufactured, any drawing modifications must still reflect the original).`}</span>
                                <br />
                                <br />
                                <p>{`Please confirm before proceeding.`}</p> 
                            </div>                                
                        </div>
                        <div className="columns is-vcentered">
                            <div className="column">
                                <div className="field">
                                    <label className="label">Has this product been manufactured before?</label> 
                                    <input onChange={onChangeIsUpissue} className="is-checkradio" id="upIssueYesCheckRadio" type="radio" value="true" name="isUpissue" checked={isUpissue} />
                                    <label htmlFor="upIssueYesCheckRadio">Yes</label>
                                    <input onChange={onChangeIsUpissue} className="is-checkradio" id="upIssueNoCheckRadio" type="radio" value="false" name="isUpissue" checked={!isUpissue} />
                                    <label htmlFor="upIssueNoCheckRadio">No</label>
                                </div>
                            </div>
                        </div>

                        {isUpissue 
                        &&
                        <>
                        <hr className="navbar-divider mt-4 mb-4" />
                        <WhatsChanged productDiff={diffProductSearchResultAndProductFormState(editObject, productFormState, designDocumentsState)} />
                        <hr className="navbar-divider mt-4 mb-4" />

                        {!isAllDrawingsDifferent(editObject.designDocuments, designDocumentsState) &&
                            <div className="message is-danger">
                                <div className="message-body p-3">
                                    <FontAwesomeIcon className="pr-2" icon={faWarning} size={'lg'} />
                                    <span>{`All drawings on the product must be updated before it may be upissued.`}</span> 
                                </div>                                
                            </div>
                        }

                        <TextAreaInputField
                            fieldLabel={`Add a comment`}
                            onChange={(e) => setUpissueForm({comment: e.target.value})}
                            fieldName={'comment'}
                            value={upIssueForm.comment}
                            errorValue={null}
                            isOptional={true}
                            helpText={`Edit comment`} />
                        </>
                        }

                        <div className="field is-grouped is-pulled-right">
                            <div className="control">
                                <button className={`button is-link ${isRequestLoading ? `is-loading` : ``}`} disabled={designDocumentsState && designDocumentsState.every(_ => _.asset.isLoading) || (isUpissue && !isAllDrawingsDifferent(editObject.designDocuments, designDocumentsState)) } onClick={onSubmitForm}>{`Submit`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={(e) => onCancelUpissueModal(e)}>Cancel</button>
                            </div>
                        </div>

                    </form>
                </BaseModal>                
            }
            

            {pageState === 'edit' && (locationState && (locationState.from === EDITS_REQUESTED)) &&
              <BaseModal
                isModalActive={isRequestedEditsModalActive}
                modalTitle={`Please confirm your changes`}
                onCancelModal={onCancelRequestedEditsModal}>
                    <form>
                        
                        <div className="message is-warning">
                            <div className="message-body p-5">
                            <div className="columns is-vcentered">
                                <div className="column is-narrow p-0">
                                <FontAwesomeIcon icon={faCircleUser} size={'2x'} />
                                </div>
                                <div className="column is-narrow">
                                <span className="pl-1 has-text-weight-semibold">{editObject?.metadata.lastModerated.byFullName}</span> previously requested edits
                                </div>
                            </div>
                            <div className="columns is-vcentered">
                            <div className="column pt-0 pl-6">
                                {editObject?.metadata.lastModerated.comment && 
                                <>
                                <span className="has-text-weight-bold is-size-4">"</span>
                                <span className="is-italic is-size-6">{editObject?.metadata.lastModerated.comment}</span>
                                <span className="has-text-weight-bold is-size-4">"</span>
                                </>
                                }
                            </div>
                            </div>
                            </div>
                        </div>

                        <TextAreaInputField
                            fieldLabel={`Add a comment`}
                            onChange={(e) => setRequestedEditsForm({comment: e.target.value})}
                            fieldName={'comment'}
                            value={requestedEditsForm.comment}
                            errorValue={null}
                            isOptional={true}
                            helpText={`Edit comment`} />

                        <div className="field is-grouped is-pulled-right">
                            <div className="control">
                                <button className={`button is-link ${isRequestLoading ? `is-loading` : ``}`} disabled={designDocumentsState && designDocumentsState.every(_ => _.asset.isLoading)} onClick={onSubmitForm}>{`Submit`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={(e) => onCancelRequestedEditsModal(e)}>Cancel</button>
                            </div>
                        </div>

                    </form>
                </BaseModal>                
            }

            {pageState === 'edit' &&
              <BaseModal
                isModalActive={isChangeRequestModalActive}
                modalTitle={`Initiate change request`}
                onCancelModal={onCancelChangeRequestModal}>
                    <form>
                        
                        <div className="message">
                            <div className="message-body p-5">
                                <FontAwesomeIcon className="pr-2" icon={faInfoCircle} size={'lg'} />
                                <span>{`Requesting changes to a live product that has been previously manufactured will require an upissue. Please confirm before proceeding.`}</span>
                            </div>                                
                        </div>

                        <div className="columns is-vcentered">
                            <div className="column">
                                <div className="field">
                                    <label className="label">Has this product been manufactured before?</label> 
                                    <input onChange={(e) => setChangeRequestForm({...changeRequestForm, isUpissue: e.target.value === 'true'})} className="is-checkradio" id="changeRequestUpIssueYesCheckRadio" type="radio" value="true" name="changeRequestIsUpissue" checked={changeRequestForm.isUpissue} />
                                    <label htmlFor="changeRequestUpIssueYesCheckRadio">Yes</label>
                                    <input onChange={(e) => setChangeRequestForm({...changeRequestForm, isUpissue: e.target.value === 'true'})} className="is-checkradio" id="changeRequestUpIssueNoCheckRadio" type="radio" value="false" name="changeRequestIsUpissue" checked={!changeRequestForm.isUpissue} />
                                    <label htmlFor="changeRequestUpIssueNoCheckRadio">No</label>
                                </div>
                            </div>
                        </div>

                        <TextAreaInputField
                            fieldLabel={`What changes would you like to request?`}
                            onChange={(e) => setChangeRequestForm({...changeRequestForm, comment: e.target.value})}
                            fieldName={'comment'}
                            value={changeRequestForm.comment}
                            errorValue={changeRequestFormErrorState.comment}
                            helpText={`Request changes`} />

                        <div className="field is-grouped is-pulled-right">
                            <div className="control">
                                <button className={`button is-link ${isChangeRequestLoading ? `is-loading` : ``}`} onClick={onSubmitChangeRequest}>{`Submit`}</button>
                            </div>
                            <div className="control">
                                <button className="button is-link is-light" onClick={(e) => onCancelChangeRequestModal(e)}>Cancel</button>
                            </div>
                        </div>

                    </form>
                </BaseModal>                
            }

            <div className="columns mt-6 pt-6">

            <div className="column is-offset-one-quarter is-half has-background-white p-0" style={{borderRadius: 6}}>

                {pageState !== 'add' && (Permissions.canDelete(loggedInUser) || Permissions.canDownloadProductionChecklist(loggedInUser)) &&
                    <div className="dropdown is-hoverable pt-5 pr-5 is-pulled-right">
                        <div className="dropdown-trigger">
                            <FontAwesomeIcon aria-haspopup="true" aria-controls="dropdown-menu4" className="is-clickable" icon={faEllipsisVertical} size={`lg`} />
                        </div>
                        <div className="dropdown-menu" id="dropdown-menu4" role="menu">
                            <div className="dropdown-content">
                                
                                {Permissions.canViewProductHistory(loggedInUser) && 
                                    <a href="#" className="dropdown-item" onClick={(e: React.MouseEvent) => { e.preventDefault(); onClickShowVersionHistoryModal()} }>
                                        <span><FontAwesomeIcon icon={faHistory} className="pr-2"/> Version History</span>
                                    </a>
                                }
                                {Permissions.canViewProductHistory(loggedInUser) && Permissions.canInitateChangeRequest(loggedInUser) && <hr className="navbar-divider" /> }
                                
                                {Permissions.canInitateChangeRequest(loggedInUser) && 
                                    <a href="#" className="dropdown-item" onClick={onClickShowChangeRequestModal}>
                                        <span><FontAwesomeIcon icon={faHand} className="pr-2"/> Request Changes</span>
                                    </a>
                                }
                                {Permissions.canInitateChangeRequest(loggedInUser) && Permissions.canDownloadProductionChecklist(loggedInUser) && <hr className="navbar-divider" /> }

                                
                                {Permissions.canDownloadProductionChecklist(loggedInUser) &&                                
                                    <PDFDownloadLink className="dropdown-item" document={<ProductionAndQualityChecklist checklistData={productToChecklistData(editObject, getProductType(editObject.productType))} />} fileName={`${productForm.formState.productCode}_Production_And_Quality_Control_CheckList.pdf`}>
                                        {({ blob, url, loading, error }) => (loading ? <FontAwesomeIcon icon={faSpinner} /> : <span><FontAwesomeIcon icon={faDownload} className="pr-2" /> Download Production Checklist</span>)}
                                    </PDFDownloadLink> 
                                }
                                
                                {Permissions.canDelete(loggedInUser) && (Permissions.canDownloadProductionChecklist(loggedInUser) || Permissions.canViewProductHistory(loggedInUser)) && <hr className="navbar-divider" /> }

                                {pageState === 'edit' && Permissions.canDelete(loggedInUser) && 
                                    <a href="#" className="dropdown-item" onClick={(e: React.MouseEvent) => { e.preventDefault(); setIsConfirmDeleteModalActive(true) } }>
                                        <span><FontAwesomeIcon icon={faTrash} className="pr-2" /> Archive</span>
                                    </a>
                                }

                            </div>
                        </div>
                    </div>
                }
               
                <div className="block pt-6 pr-6 pl-6 p-2 mb-0">
                    {pageState === 'edit' ? 
                    <>
                    <div className="columns is-vcentered">
                        <div className="column">
                            <h1 className="title is-3">{`Edit Product`}</h1>
                        </div>
                        <div className="column">
                            {editObject?.metadata.lastUpissued ?
                                <IssuesDropwdown metadata={editObject.metadata} productCode={productCode} />
                            : editObject?.metadata?.created ?
                                <IssueNumberTag metadataInfo={editObject?.metadata?.created} issueNumber={editObject?.metadata.issueNumber} actionText={`Created by `} />
                            : null
                            }
                        </div>
                    </div>
                    </>
                    : pageState === 'view' ? 
                    <h1 className="title is-3">{`View Product`}</h1>
                    : 
                    <h1 className="title is-3">{`Add a new Product`}</h1>
                    }
                </div>

                <ProductForm
                    isMerchant={isMerchant(loggedInUser)}
                    formView={pageState}
                    productForm={productForm}
                    designDocumentsForm={designDocumentsForm}
                    productTypes={productTypes}
                    pipeOpenings={pipeOpenings}
                    pipeMaterials={pipeMaterials}
                    flowControlDevices={flowControlDevices} />

                    <div className="block pt-4 pb-6 pl-6 pr-6 mb-4">
                        {pageState !== 'view' &&
                            <div className="field is-grouped is-pulled-right">
                                <div className="control">
                                    {renderSubmitButton()}
                                </div>
                                <div className="control">
                                    {pageState === 'edit'  ?
                                        <button className="button is-link is-light" onClick={onCancelEdit}>Cancel</button>
                                    :
                                        <button className="button is-link is-light" onClick={onClear}>Clear</button>   
                                    }
                                </div>
                            </div>
                        }
                    </div>

            </div>                    
                
            </div>
        </div>
    )
}

export { IndexProductScreen } 